import { useEffect, useMemo, useRef } from 'react'
import type { DialogProps } from '@mui/material/Dialog'
import type { SxProps, Theme } from '@mui/material/styles'

declare module '@mui/material/Modal' {
    /** Modal's root slot is a styled component at runtime, so `sx` is honoured; the MUI 5 typings omit it. */
    interface ModalComponentsPropsOverrides {
        sx?: SxProps<Theme>
    }
}
import { useCloseOnPointerDownOutside } from '../../hooks/useCloseOnPointerDownOutside'

export type NonModalDialogOptions = {
    /** Keep open while interacting with these elements (e.g. the trigger button). */
    clickAwayIgnoreRefs?: React.RefObject<HTMLElement | null>[]
    /** Keep open while the event target is inside these selectors. */
    clickAwayIgnoreSelectors?: string[]
    /** Called on Escape instead of `onClose` when provided (e.g. nested sub-views). */
    onEscape?: () => void
}

/**
 * Nested menus / popovers / autocompletes render in portals outside the dialog paper.
 * Clicking them must not dismiss the dialog.
 */
const DEFAULT_DIALOG_IGNORE_SELECTORS = [
    '.MuiPopover-root',
    '.MuiMenu-root',
    '.MuiAutocomplete-popper',
    '.MuiTooltip-popper',
    '.MuiPickersPopper-root'
]

/**
 * Makes a MUI Dialog behave like a non-modal panel:
 * - page outside the dialog stays interactive (no backdrop, pointer-events passthrough)
 * - closes on pointer down outside the dialog paper
 *
 * Spread `dialogProps` onto the `<Dialog>` and pass `paperRef` to `PaperProps.ref`.
 */
export function useNonModalDialog(
    open: boolean,
    onClose: () => void,
    options: NonModalDialogOptions = {}
): { dialogProps: Partial<DialogProps>; paperRef: React.RefObject<HTMLDivElement | null> } {
    const paperRef = useRef<HTMLDivElement>(null)
    const { clickAwayIgnoreRefs, clickAwayIgnoreSelectors, onEscape } = options

    const ignoreSelectors = useMemo(
        () => [...DEFAULT_DIALOG_IGNORE_SELECTORS, ...(clickAwayIgnoreSelectors ?? [])],
        [clickAwayIgnoreSelectors]
    )

    useCloseOnPointerDownOutside(open, onClose, {
        containerRef: paperRef,
        ignoreRefs: clickAwayIgnoreRefs,
        ignoreSelectors
    })

    // hideBackdrop + no focus trap means the Dialog's own Escape handling does not
    // fire, so close on Escape explicitly.
    useEffect(() => {
        if (!open) return
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                event.stopImmediatePropagation()
                ;(onEscape ?? onClose)()
            }
        }
        document.addEventListener('keydown', handleKeyDown, true)
        return () => document.removeEventListener('keydown', handleKeyDown, true)
    }, [open, onClose, onEscape])

    const dialogProps: Partial<DialogProps> = {
        hideBackdrop: true,
        disableScrollLock: true,
        disableEnforceFocus: true,
        disableAutoFocus: true,
        disableRestoreFocus: true,
        sx: {
            pointerEvents: 'none'
        },
        slotProps: {
            root: {
                sx: {
                    pointerEvents: 'none'
                }
            }
        }
    }

    return { dialogProps, paperRef }
}
